import { useMemo } from 'react'
import styled from 'styled-components'
import { Text, Flex, CloseIcon, IconButton, useMatchBreakpoints } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { usePhishingBanner } from '@pancakeswap/utils/user'

const Container = styled(Flex)`
  overflow: hidden;
  height: 100%;
  padding: 12px;
  align-items: center;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-bottom: 2px solid ${({ theme }) => theme.colors.warning};
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 0px;
  }
`

const InnerContainer = styled(Flex)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`

const SpeechBubble = styled.div`
  background: ${({ theme }) => theme.colors.input};
  border-radius: ${({ theme }) => theme.radii.default};
  padding: 8px;
  width: 60%;
  height: 80%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  & ${Text} {
    flex-shrink: 0;
    margin-right: 4px;
  }
`

const domain = 'https://apps.cadinu.io'
const host = 'apps.cadinu.io'

/*
 * Phones (site review 59 #2, 2026-10-07): the two-line warning took 84 px above the header, and with the install line it pushed the swap
 * button under the bottom bar on a first visit (y 829 against the bar's 793 at 390x844). One 44 px line now (Menu's
 * TOP_BANNER_HEIGHT_MOBILE), the same warning colour and close button; it never truncates the address.
 */
const Line = styled.p`
  flex: 1;
  min-width: 0;
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.textSubtle};
  > b {
    flex: none;
    color: ${({ theme }) => theme.colors.text};
    font-weight: 700;
  }
`
const Label = styled.span`
  flex: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.warning};
`
const Filler = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PhishingWarningBanner: React.FC<React.PropsWithChildren> = () => {
  const { t } = useTranslation()
  const [, hideBanner] = usePhishingBanner()
  const { isMobile, isMd } = useMatchBreakpoints()
  const closeLabel = t('Close phishing warning')
  // Narrow widths get the short form so the banner re-flows to two lines instead of three at 360px.
  const warningTextAsParts = useMemo(() => {
    const warningText =
      isMobile || isMd
        ? t('check the URL: %domain%', { domain })
        : t("please make sure you're visiting %domain% - check the URL carefully.", { domain })
    return warningText.split(/(https:\/\/apps.cadinu.io)/g)
  }, [t, isMobile, isMd])
  const warningTextComponent = (
    <>
      <Text as="span" color="warning" small bold textTransform="uppercase">
        {t('Phishing warning: ')}
      </Text>
      {warningTextAsParts.map((text, i) => (
        <Text
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          small
          as="span"
          bold={text === domain}
          color={text === domain ? 'text' : 'textSubtle'}
        >
          {text}
        </Text>
      ))}
    </>
  )
  return (
    <Container className="warning-banner" style={isMobile || isMd ? { padding: '0 0 0 12px' } : undefined}>
      {isMobile || isMd ? (
        <>
          <Line data-testid="phishing-line">
            <Label>{t('Phishing warning')}:</Label>
            <Filler>{t('only use')}</Filler>
            <b>{host}</b>
          </Line>
          <IconButton onClick={hideBanner} variant="text" aria-label={closeLabel} style={{ minWidth: 44, minHeight: 44, height: 44 }}>
            <CloseIcon color="text" />
          </IconButton>
        </>
      ) : (
        <>
          <InnerContainer>
            {/* <img
              src="/images/decorations/phishing-warning-bunny.webp"
              alt="phishing-warning"
              width="92px"
              onError={(e) => {
                const fallbackSrc = '/images/decorations/phishing-warning-bunny.png'
                if (!e.currentTarget.src.endsWith(fallbackSrc)) {
                  // eslint-disable-next-line no-param-reassign
                  e.currentTarget.src = fallbackSrc
                }
              }}
            /> */}
            <SpeechBubble>{warningTextComponent}</SpeechBubble>
          </InnerContainer>
          <IconButton onClick={hideBanner} variant="text" aria-label={closeLabel} style={{ minWidth: 44, minHeight: 44 }}>
            <CloseIcon color="text" />
          </IconButton>
        </>
      )}
    </Container>
  )
}

export default PhishingWarningBanner
